import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import { WA_TAGS } from "../../../Queries/WhatsApp";
import {
  fetchWaStatus, fetchWaQuickReplies, fetchWaAssignees, setWaActive, clearWaPendingNew,
} from "../../../Slices/whatsappSlice";
import ConversationList from "./ConversationList";
import ConversationPane from "./ConversationPane";
import ContextPanel from "./ContextPanel";
import NewConversationPanel from "./NewConversationPanel";
import LinkRecordPanel from "./LinkRecordPanel";
import "./whatsapp.css";

const CLOCK_TICK_MS = 30 * 1000;
const COUNTS_REFRESH_MS = 60 * 1000;

const Gate = ({ icon, title, children }) => (
  <div className="wa-root"><div className="wa-center"><div className="wa-gate">
    <i className={`${icon} wa-empty__icon`} style={{ fontSize: 40 }} />
    <h3>{title}</h3>
    {children}
  </div></div></div>
);

const WhatsAppInbox = () => {
  const dispatch = useDispatch();
  const { status, statusLoaded, statusError, activeId, details, list, pendingNew } = useSelector((s) => s.whatsapp);
  const [now, setNow] = useState(Date.now());
  const [panel, setPanel] = useState(null); // inbox-level panels: { type: "new" | "link", ... }
  const [paneHasPanel, setPaneHasPanel] = useState(false); // conversation pane's own panels (templates, quick replies, image)
  const [contextOpen, setContextOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const searchInputRef = useRef(null);

  const detail = activeId ? details[activeId] : null;
  const listItem = list.items.find((c) => c.id === activeId);
  // The list item is updated live by sockets; prefer it over the (fetched) detail copy.
  const conversation = listItem || detail?.conversation || null;
  const anyPanel = !!panel || paneHasPanel;

  // ── bootstrap ──
  useEffect(() => {
    dispatch(fetchWaStatus());
  }, [dispatch]);

  useEffect(() => {
    if (!status?.canUseInbox) return;
    dispatch(fetchWaQuickReplies());
    dispatch(fetchWaAssignees());
    client.query({ query: WA_TAGS, fetchPolicy: "network-only" }).then(({ data }) => setTags(data.whatsappTags || [])).catch(() => {});
  }, [dispatch, status?.canUseInbox]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    const c = setInterval(() => dispatch(fetchWaStatus()), COUNTS_REFRESH_MS);
    return () => { clearInterval(t); clearInterval(c); };
  }, [dispatch]);

  // A lead/client page asked to message someone → open the New message panel pre-filled.
  useEffect(() => {
    if (!pendingNew || !status?.canUseInbox) return;
    setPanel({ type: "new", initial: pendingNew, key: pendingNew.nonce });
    dispatch(clearWaPendingNew());
  }, [pendingNew, status?.canUseInbox, dispatch]);

  // Switching conversations closes inbox-level panels that belong to the old one.
  useEffect(() => {
    setPanel((p) => (p?.type === "link" ? null : p));
  }, [activeId]);

  // ── keyboard: "/" focuses search (when not typing), Esc closes details drawer ──
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = ["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable;
      if (e.key === "/" && !typing && !anyPanel) {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === "Escape" && !anyPanel && contextOpen) {
        setContextOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [anyPanel, contextOpen]);

  // ── gates ──
  if (!statusLoaded) {
    return <Gate icon="fa-solid fa-spinner wa-spin" title="Loading WhatsApp…" />;
  }
  if (statusError) {
    return (
      <Gate icon="fa-solid fa-triangle-exclamation" title="WhatsApp is unavailable">
        <p>{statusError}</p>
        <button type="button" className="wa-btn" onClick={() => dispatch(fetchWaStatus())}>Retry</button>
      </Gate>
    );
  }
  if (!status?.canUseInbox) {
    return (
      <Gate icon="fa-solid fa-lock" title="No access to the WhatsApp inbox">
        <p>Ask an administrator to grant <b>WhatsApp Inbox</b> in Access Management.</p>
      </Gate>
    );
  }
  if (!status.enabled) {
    return (
      <Gate icon="fa-solid fa-plug-circle-xmark" title="WhatsApp isn't connected yet">
        <p>The server is missing its WhatsApp credentials{status.missing?.length ? <>: <code>{status.missing.join(", ")}</code></> : ""}. Add them to the backend environment and restart.</p>
      </Gate>
    );
  }

  return (
    <div className={`wa-root ${activeId ? "has-active" : ""} ${contextOpen ? "is-context-open" : ""}`}>
      <ConversationList searchInputRef={searchInputRef} onNewMessage={() => setPanel({ type: "new" })} tags={tags} now={now} />

      {activeId ? (
        <ConversationPane
          key={activeId}
          conversationId={activeId}
          onBack={() => dispatch(setWaActive(null))}
          onPanelChange={setPaneHasPanel}
          headerActions={(
            <button type="button" className="wa-icon-btn" onClick={() => setContextOpen((v) => !v)} aria-label="Toggle details" title="Details" aria-pressed={contextOpen}>
              <i className="fa-solid fa-circle-info" />
            </button>
          )}
        />
      ) : (
        <div className="wa-thread" role="region" aria-label="Conversation">
          <div className="wa-empty">
            <i className="fa-brands fa-whatsapp wa-empty__icon" style={{ fontSize: 48 }} />
            <div className="wa-empty__title">Select a conversation</div>
            <div>Pick a chat on the left, or start a new one with an approved template.</div>
            <div className="wa-help"><span className="wa-kbd">/</span> search · <span className="wa-kbd">↑</span><span className="wa-kbd">↓</span> move · <span className="wa-kbd">Esc</span> close panels</div>
          </div>
        </div>
      )}

      {conversation && detail && !detail.error && (
        <ContextPanel detail={{ ...detail, conversation }} onLink={() => setPanel({ type: "link" })} onClose={contextOpen ? () => setContextOpen(false) : null} now={now} />
      )}

      {panel?.type === "new" && (
        <NewConversationPanel key={panel.key || "new"} initial={panel.initial} onClose={() => setPanel(null)} />
      )}
      {panel?.type === "link" && conversation && (
        <LinkRecordPanel conversation={conversation} candidates={detail?.linkCandidates} onClose={() => setPanel(null)} />
      )}
    </div>
  );
};

export default WhatsAppInbox;
